import {useMutation} from '@github-ui/react-query'
import {prioritizePinnedIssuesMutation as prioritizePinnedIssuesQueryId} from '@github-ui/server-persisted-queries'
import type {PrioritizePinnedIssuesMutationMutation} from '@github-ui/server-persisted-queries/types/prioritizePinnedIssuesMutation'
import {reactFetchJSON} from '@github-ui/verified-fetch'
import {useCallback} from 'react'
import {useRelayEnvironment} from 'react-relay'
import {commitLocalUpdate} from 'react-relay/hooks'
import type {Disposable, RecordSourceProxy} from 'relay-runtime'

const FAILURE_MESSAGE = 'Could not reorder pinned issues'

type PrioritizePinnedIssuesContext = {
  relayUpdate: Disposable
}

type GraphQLResponse<TData> = {
  data?: TData | null
  errors?: ReadonlyArray<{message?: string}> | null
}

async function submitPrioritizePinnedIssuesMutation({
  owner,
  repository,
  repositoryId,
  issueIds,
}: {
  owner: string
  repository: string
  repositoryId: string
  issueIds: readonly string[]
}) {
  const path = `/${encodeURIComponent(owner)}/${encodeURIComponent(repository)}/issues/pinned_issues`
  const response = await reactFetchJSON(`${path}?${new URLSearchParams({queryId: prioritizePinnedIssuesQueryId})}`, {
    method: 'PUT',
    body: {issueIds},
  })
  if (!response.ok) throw new Error(`${FAILURE_MESSAGE} (${response.status})`)

  const result = (await response.json()) as GraphQLResponse<PrioritizePinnedIssuesMutationMutation>
  const topLevelError = result.errors?.[0]?.message

  const resultRepository = result.data?.prioritizePinnedIssues?.repository
  const nodes = resultRepository?.pinnedIssues?.nodes
  if (resultRepository?.id !== repositoryId || !nodes) throw new Error(topLevelError ?? FAILURE_MESSAGE)

  const confirmedIssueIds = nodes.map(node => node?.issue.id)
  if (
    confirmedIssueIds.some(id => !id) ||
    confirmedIssueIds.length !== issueIds.length ||
    [...confirmedIssueIds].sort().some((id, index) => id !== [...issueIds].sort()[index])
  ) {
    throw new Error(topLevelError ?? FAILURE_MESSAGE)
  }

  return confirmedIssueIds as string[]
}

function reorderRelayPinnedIssues(store: RecordSourceProxy, repositoryId: string, issueIds: readonly string[]) {
  const pinnedIssues = store.get(repositoryId)?.getLinkedRecord('pinnedIssues', {first: 3})
  const nodes = pinnedIssues?.getLinkedRecords('nodes')
  if (!pinnedIssues || !nodes) return

  const nodesByIssueId = new Map(nodes.map(node => [node.getLinkedRecord('issue')?.getDataID(), node]))
  const orderedNodes = issueIds.flatMap(issueId => {
    const node = nodesByIssueId.get(issueId)
    return node ? [node] : []
  })
  if (orderedNodes.length === nodes.length) pinnedIssues.setLinkedRecords(orderedNodes, 'nodes')
}

export function usePrioritizePinnedIssuesMutation({
  owner,
  repository,
  repositoryId,
  onError,
}: {
  owner: string
  repository: string
  repositoryId: string
  onError?: (error: Error) => void
}) {
  const environment = useRelayEnvironment()
  const {mutate} = useMutation<string[], Error, readonly string[], PrioritizePinnedIssuesContext>({
    scope: {id: `prioritize-pinned-issues:${repositoryId}`},
    mutationFn: issueIds => submitPrioritizePinnedIssuesMutation({owner, repository, repositoryId, issueIds}),
    onMutate: issueIds => ({
      relayUpdate: environment.applyUpdate({
        storeUpdater: store => reorderRelayPinnedIssues(store, repositoryId, issueIds),
      }),
    }),
    onError: (error, _issueIds, context) => {
      context?.relayUpdate.dispose()
      onError?.(error)
    },
    onSuccess: (confirmedIssueIds, _issueIds, context) => {
      commitLocalUpdate(environment, store => reorderRelayPinnedIssues(store, repositoryId, confirmedIssueIds))
      context.relayUpdate.dispose()
    },
  })

  return useCallback((issueIds: readonly string[]) => mutate(issueIds), [mutate])
}
